import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  FaCalendarAlt,
  FaSortDown,
  FaRedoAlt,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight
} from "react-icons/md";

// ------------------- HELPER FUNCTIONS -------------------
function currency(n) {
  if (n === undefined || n === null) return "0.00";
  return parseFloat(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toDate(d) {
  return typeof d === "string" ? new Date(d + "T00:00:00") : d;
}

function isValid(s) {
  const d = new Date(s);
  return !Number.isNaN(d.getTime());
}

// ------------------- MAIN COMPONENT -------------------
export default function AccountStatement() {
  const [activeTab, setActiveTab] = useState("all");
  const [showMobile, setShowMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const navigate = useNavigate();
  const formatDateForAPI = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };


  const [fromDate, setFromDate] = useState("");
  const [toDateState, setToDate] = useState("");
  const [pageSize, setPageSize] = useState(100);
  const [applied, setApplied] = useState({
    from: "",
    to: "",
    size: 100,
  });

  const [allData, setAllData] = useState([]);
  const [plData, setPlData] = useState([]);

  const [accountData, setAccountData] = useState([]);

  const [plSummary, setPlSummary] = useState({
    lena: 0,
    dena: 0,
    balance: 0
  });

  const [accountSummary, setAccountSummary] = useState({
    credit: 0,
    debit: 0,
    total: 0
  });

  const baseUrl = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem("user_id");
  const admin_id = localStorage.getItem("admin_id");

  const fetchAllData = useCallback(async () => {
    if (!userId) {
      setError("User not logged in");
      setLoading(false);
      return;
    }

    try {
      if (allData.length === 0) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      setError(null);
      const startDate = formatDateForAPI(applied.from);
      const endDate = formatDateForAPI(applied.to);

      const params = {
        user_id: userId,
        admin_id: admin_id,
        start_date: applied.from || "",
        end_date: applied.to || "",
        page: currentPage,
        limit: applied.size,
      };

      const res = await axios.post(`${baseUrl}/all-statement-ladger`, params);

      if (res.data.success) {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setAllData(list);
        setTotalItems(res.data.pagination?.total_records || list.length);
        setTotalPages(res.data.pagination?.total_pages || 1);
      } else {
        setAllData([]);
        setTotalItems(0);
        setTotalPages(1);
        setError(res.data.message || "No data found");
      }
    } catch (err) {
      console.error("Fetch All Error:", err);
      setError("Network error. Try again.");
      setAllData([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [baseUrl, userId, admin_id, applied, currentPage]);

  const fetchPLData = useCallback(async () => {
    if (!userId) {
      setError("User not logged in");
      return;
    }

    try {
      // setLoading(true);
      if (plData.length === 0) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      setError(null);

      const startDate = formatDateForAPI(applied.from);
      const endDate = formatDateForAPI(applied.to);

      const params = {
        user_id: userId,
        admin_id: admin_id,
        start_date: startDate,
        end_date: endDate,
        page: currentPage,
        limit: applied.size,
      };

      const res = await axios.post(`${baseUrl}/user-profit-loss`, params);

      if (res.data.success) {
        // Set P&L summary if available
        setPlSummary({
          lena: res.data.lena || 0,
          dena: res.data.dena || 0,
          balance: res.data.balance || 0
        });

        // Get the data array
        const list = Array.isArray(res.data.data?.data)
          ? res.data.data.data
          : (Array.isArray(res.data.data)
            ? res.data.data
            : []);

        setPlData(list);

        // CORRECTED: Use pagination from res.data.pagination
        if (res.data.pagination) {
          setTotalItems(res.data.pagination.total_records || 0);
          setTotalPages(res.data.pagination.total_pages || 1);
        } else if (res.data.data?.pagination) {
          // Fallback if pagination is nested in data
          setTotalItems(res.data.data.pagination.total_records || 0);
          setTotalPages(res.data.data.pagination.total_pages || 1);
        } else {
          // Default fallback
          setTotalItems(list.length);
          setTotalPages(1);
        }
      } else {
        setPlData([]);
        setPlSummary({ lena: 0, dena: 0, balance: 0 });
        setTotalItems(0);
        setTotalPages(1);
        setError(res.data.message || "No P&L data found");
      }
    } catch (err) {
      console.error("P&L Fetch Error:", err);
      setError("Network error while fetching P&L data.");
      setPlData([]);
      setPlSummary({ lena: 0, dena: 0, balance: 0 });
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setTableLoading(false)
    }
  }, [baseUrl, userId, admin_id, applied, currentPage]);

  const fetchAccountData = useCallback(async () => {
    if (!userId) {
      setError("User not logged in");
      setLoading(false);
      return;
    }

    try {
      if (accountData.length === 0) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      // setLoading(true);
      setError(null);

      const startDate = formatDateForAPI(applied.from);
      const endDate = formatDateForAPI(applied.to);

      const params = {
        user_id: userId,
        admin_id: admin_id,
        page: currentPage,
        limit: applied.size,
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const res = await axios.post(`${baseUrl}/get-statement-user-ladger`, params);

      if (res.data.success) {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setAccountData(list);
        let credit = 0;
        let debit = 0;

        list.forEach(t => {
          const amount = parseFloat(t.amount) || 0;
          const isCredit = t.type === "deposit" || t.type === "bonus" || t.type === "commission";
          const isDebit = t.type === "withdraw" || t.type === "transfer";
          if (isCredit) {
            credit += amount;
          } else if (isDebit) {
            debit += amount;
          }
        });
        setAccountSummary({
          credit,
          debit,
          total: credit - debit
        });
        setTotalItems(res.data.pagination?.total_records || list.length);
        setTotalPages(res.data.pagination?.total_pages || 1);
      } else {
        setAccountData([]);
        setAccountSummary({ credit: 0, debit: 0, total: 0 });
        setError(res.data.message || "No account data found");
      }
    } catch (err) {
      console.error("Account Fetch Error:", err);
      setError("Network error while fetching account data.");
      setAccountData([]);
      setAccountSummary({ credit: 0, debit: 0, total: 0 });
    } finally {
      setLoading(false);
      setTableLoading(false)
    }
  }, [baseUrl, userId, admin_id, applied, currentPage]);

  useEffect(() => {
    switch (activeTab) {
      case "all":
        fetchAllData();
        break;
      case "pl":
        fetchPLData();
        break;
      case "account":
        fetchAccountData();
        break;
      default:
        fetchAllData();
    }
  }, [activeTab, fetchAllData, fetchPLData, fetchAccountData]);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [applied]);

  // -------------------- APPLY FILTERS -----------------------
  const applyFilters = () => {

    const newFrom = fromDate;
    const newTo = toDateState;

    setCurrentPage(1);

    setApplied({
      from: newFrom,
      to: newTo,
      size: pageSize,
    });
  };

  const resetFilters = () => {

    setFromDate("");
    setToDate("");

    setPageSize(100);

    setCurrentPage(1);

    setApplied({
      from: "",
      to: "",
      size: 100,
    });
  };

  // ----------------- PAGINATION ------------------
  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 2;
    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(
      totalPages,
      startPage + maxVisiblePages - 1
    );

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(
        1,
        endPage - maxVisiblePages + 1
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handleNext = () => {
    if (currentPage < totalPages)
      setCurrentPage((prev) => prev + 1);
  };

  const handlePageClick = (pageNo) => {
    setCurrentPage(pageNo);
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true // AM/PM format
      });
    } catch (e) {
      return dateString;
    }
  };

  // Format time for display
  const formatDisplayTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return "";
    }
  };

  // Calculate rows for "all" tab - EXACT API FIELDS
  const rows = useMemo(() => {
    if (activeTab !== "all") return [];

    return allData.map((t, idx) => {
      const sn = (currentPage - 1) * applied.size + idx + 1;

      return {
        sn,
        id: t._id || idx,
        date: formatDisplayDate(t.date),
        description: t.description || "N/A",
        prev_balance: t.prev_balance || 0,
        credit: t.credit || 0,
        debit: t.debit || 0,
        commission: t.commission || 0,
        balance: t.balance || 0,
        tr_status: t.tr_status || 0
      };
    });
  }, [allData, activeTab, currentPage, applied]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // Calculate totals for each tab
  const allTabTotals = useMemo(() => {
    return {
      totalCredit: allData.reduce((sum, t) => sum + (parseFloat(t.credit) || 0), 0),
      totalDebit: allData.reduce((sum, t) => sum + (parseFloat(t.debit) || 0), 0),
      totalBalance: allData.reduce((sum, t) => sum + (parseFloat(t.balance) || 0), 0)
    };
  }, [allData]);

  const plTabTotals = useMemo(() => {
    return {
      totalWins: plData
        .filter(t => t.win_loss === "WIN")
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
      totalLosses: plData
        .filter(t => t.win_loss === "LOSS")
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
      netPL: plData.reduce((sum, t) => {
        if (t.win_loss === "WIN") return sum + (parseFloat(t.amount) || 0);
        if (t.win_loss === "LOSS") return sum - (parseFloat(t.amount) || 0);
        return sum;
      }, 0)
    };
  }, [plData]);

  const accountTabTotals = useMemo(() => {
    return {
      totalCredit: accountData
        .filter(t => t.type === "deposit" || t.type === "bonus" || t.type === "commission")
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
      totalDebit: accountData
        .filter(t => t.type === "withdraw" || t.type === "transfer")
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
      netBalance: accountData.reduce((sum, t) => {
        const amount = parseFloat(t.amount) || 0;
        if (t.type === "deposit" || t.type === "bonus" || t.type === "commission") {
          return sum + amount;
        } else if (t.type === "withdraw" || t.type === "transfer") {
          return sum - amount;
        }
        return sum;
      }, 0)
    };
  }, [accountData]);

  const handlePrev = () => {
    if (currentPage > 1)
      setCurrentPage((prev) => prev - 1);
  };

  // ----------------- RENDER ------------------
  return (
    <div className="mt-3">
      <div className="card">
        <div className="card-header bg-color-black">
          <div className="d-flex align-items-center justify-content-between">
            <h3 className="card-title text-white">Account Statement</h3>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        <div className="card-body">
          {/* TABS */}
          <ul className="nav nav-tabs mb-3" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "all" ? "active" : ""}`}
                onClick={() => handleTabChange("all")}
                type="button"
              >
                All
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "pl" ? "active" : ""}`}
                onClick={() => handleTabChange("pl")}
                type="button"
              >
                P&L
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "account" ? "active" : ""}`}
                onClick={() => handleTabChange("account")}
                type="button"
              >
                Account
              </button>
            </li>
          </ul>

          {/* ALL TAB */}
          {activeTab === "all" && (
            <div>
              <div className="form-design-fillter gap-2 d-flex justify-content-between align-items-end flex-wrap mb-3">
                <div className="form_latest_design">
                  <div className="label"><label>From:</label></div>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div className="form_latest_design">
                  <div className="label"><label>To:</label></div>
                  <input
                    type="date"
                    className="form-control"
                    value={toDateState}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div className="form_latest_design">
                  <div className="label"><label>Show:</label></div>
                  <select
                    className="form-control"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="refreshbutton"
                    onClick={applyFilters}
                    disabled={loading}
                  >
                    Filter
                  </button>
                  <button
                    className="refreshbutton"
                    onClick={resetFilters}
                    disabled={loading}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {tableLoading && (
                <div className="table-loader">
                  <div className="spinner-border text-primary" />
                </div>
              )}



              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>S.NO</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Prev Balance</th>
                      <th>Credit</th>
                      <th>Debit</th>
                      <th>Balance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No transactions in the selected range.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.sn}</td>
                          <td>{r.date}</td>
                          <td>{r.description}</td>
                          <td className="text-end">₹{currency(r.prev_balance)}</td>
                          <td className="text-end" style={{ color: r.credit > 0 ? '#28a745' : '#000' }}>
                            {r.credit > 0 ? `+ ₹${currency(r.credit)}` : "—"}
                          </td>
                          <td className="text-end" style={{ color: r.debit > 0 ? '#dc3545' : '#000' }}>
                            {r.debit > 0 ? `- ₹${currency(r.debit)}` : "—"}
                          </td>
                          <td className="text-end fw-bold">₹{currency(r.balance)}</td>
                          <td className="text-end fw-bold">{r.tr_status}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {/* {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <p className="text-muted mb-0">
                        Showing {(currentPage - 1) * applied.size + 1} to{" "}
                        {Math.min(currentPage * applied.size, totalItems)} of{" "}
                        {totalItems} entries
                      </p>

                      <div className="d-flex gap-2 align-items-center">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => goToPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <FaChevronLeft />
                        </button>

                        {getPageNumbers().map((p) => (
                          <button
                            key={p}
                            className={`btn ${currentPage === p ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => goToPage(p)}
                          >
                            {p}
                          </button>
                        ))}

                        <button
                          className="btn btn-outline-primary"
                          onClick={() => goToPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  )} */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">

                  {/* Showing Entries */}
                  <div className="sohwingallentries">
                    Showing{" "}
                    {(currentPage - 1) * applied.size + 1}
                    {" "}to{" "}
                    {(currentPage - 1) * applied.size + rows.length}
                    {" "}of{" "}
                    {totalItems} entries
                  </div>

                  {/* Pagination */}
                  <div className="paginationall d-flex align-items-center gap-2">

                    {/* PREV */}
                    <button
                      className="d-flex justify-content-center align-items-center"
                      disabled={currentPage === 1}
                      onClick={handlePrev}
                    >
                      <MdOutlineKeyboardArrowLeft />
                    </button>

                    {/* PAGE NUMBERS */}
                    <div className="d-flex gap-1">
                      {getPageNumbers().map((pageNo) => (
                        <button
                          key={pageNo}
                          className={`paginationnumber ${pageNo === currentPage ? "active" : ""
                            }`}
                          onClick={() => handlePageClick(pageNo)}
                        >
                          {pageNo}
                        </button>
                      ))}
                    </div>

                    {/* NEXT */}
                    <button
                      className="d-flex justify-content-center align-items-center"
                      disabled={currentPage === totalPages}
                      onClick={handleNext}
                    >
                      <MdOutlineKeyboardArrowRight />
                    </button>

                  </div>
                </div>
              )}


            </div>
          )}

          {/* P&L TAB */}
          {activeTab === "pl" && (
            <div>
              <div className="form-design-fillter gap-2 d-flex justify-content-between align-items-end flex-wrap mb-3">
                <div className="form_latest_design">
                  <div className="label"><label>From:</label></div>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}

                  />
                </div>

                <div className="form_latest_design">
                  <div className="label"><label>To:</label></div>
                  <input
                    type="date"
                    className="form-control"
                    value={toDateState}
                    onChange={(e) => setToDate(e.target.value)}
                    min={fromDate}
                  />
                </div>

                <div className="form_latest_design">
                  <div className="label"><label>Show:</label></div>
                  <select
                    className="form-control"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="refreshbutton"
                    onClick={applyFilters}
                    disabled={loading}
                  >
                    Filter
                  </button>
                  <button
                    className="refreshbutton"
                    onClick={resetFilters}
                    disabled={loading}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <>
                  {tableLoading && (
                    <div className="table-loader">
                      <div className="spinner-border text-primary" />
                    </div>
                  )}

                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>S.NO</th>
                          <th>Date & Time</th>
                          <th>Description</th>
                          <th>Prev.Bal</th>
                          <th>Credit</th>
                          <th>Debit</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plData.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center">
                              No P&L data available
                            </td>
                          </tr>
                        ) : (

                          plData.map((t, i) => {
                            const serialNo =
                              (currentPage - 1) * applied.size + i + 1;
                            return (

                              <tr key={t._id || i}>
                                <td>{serialNo}</td>
                                <td>
                                  <div>{formatDisplayDate(t.created_at)}</div>
                                  <small className="text-muted">{formatDisplayTime(t.created_at)}</small>
                                </td>
                                <td>
                                  {t.win_loss || "N/A"} // {t.full_team_name || t.remark}
                                </td>
                                <td className="text-end">
                                  {t.before_balance_from}
                                </td>
                                <td
                                  className="text-end"
                                  style={{ color: t.win_loss === "WIN" ? '#28a745' : '#6c757d' }}
                                >
                                  {t.win_loss === "WIN" ? t.amount : "-"}
                                </td>
                                <td
                                  className="text-end"
                                  style={{ color: t.win_loss === "LOSS" ? '#dc3545' : '#6c757d' }}
                                >
                                  {t.win_loss === "LOSS" ? t.amount : "-"}
                                </td>
                                <td className="text-center">{t.wallet_amount || "N/A"}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination for P&L */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">

                      {/* Showing Entries */}
                      <div className="sohwingallentries">
                        Showing{" "}
                        {(currentPage - 1) * applied.size + 1}
                        {" "}to{" "}
                        {(currentPage - 1) * applied.size + plData.length}
                        {" "}of{" "}
                        {totalItems} entries
                      </div>

                      {/* Pagination */}
                      <div className="paginationall d-flex align-items-center gap-2">

                        {/* PREV */}
                        <button
                          className="d-flex justify-content-center align-items-center"
                          disabled={currentPage === 1}
                          onClick={handlePrev}
                        >
                          <MdOutlineKeyboardArrowLeft />
                        </button>

                        {/* PAGE NUMBERS */}
                        <div className="d-flex gap-1">
                          {getPageNumbers().map((pageNo) => (
                            <button
                              key={pageNo}
                              className={`paginationnumber ${pageNo === currentPage ? "active" : ""
                                }`}
                              onClick={() => handlePageClick(pageNo)}
                            >
                              {pageNo}
                            </button>
                          ))}
                        </div>

                        {/* NEXT */}
                        <button
                          className="d-flex justify-content-center align-items-center"
                          disabled={currentPage === totalPages}
                          onClick={handleNext}
                        >
                          <MdOutlineKeyboardArrowRight />
                        </button>

                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ACCOUNT TAB */}
          {activeTab === "account" && (
            <div>
              <div className="form-design-fillter gap-2 d-flex justify-content-between align-items-end flex-wrap mb-3">
                <div className="form_latest_design">
                  <div className="label"><label>From:</label></div>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div className="form_latest_design">
                  <div className="label"><label>To:</label></div>
                  <input
                    type="date"
                    className="form-control"
                    value={toDateState}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div className="form_latest_design">
                  <div className="label"><label>Show:</label></div>
                  <select
                    className="form-control"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="refreshbutton"
                    onClick={applyFilters}
                    disabled={loading}
                  >
                    Filter
                  </button>
                  <button
                    className="refreshbutton"
                    onClick={resetFilters}
                    disabled={loading}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <>
                  {tableLoading && (
                    <div className="table-loader">
                      <div className="spinner-border text-primary" />
                    </div>
                  )}

                  <div className="table-responsive position-relative">
                    <table className="table table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Category</th>
                          <th>From/To</th>
                          <th>Credit</th>
                          <th>Debit</th>
                          <th>Remarks</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accountData.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center">
                              No transactions available
                            </td>
                          </tr>
                        ) : (
                          accountData.map((t, i) => {
                            const isCredit = t.type === "deposit" || t.type === "bonus" || t.type === "commission";
                            const isDebit = t.type === "withdraw" || t.type === "transfer";
                            const amount = parseFloat(t.amount) || 0;

                            let categoryColor = "#6c757d";
                            if (t.type === "deposit") categoryColor = "#28a745";
                            else if (t.type === "withdraw") categoryColor = "#dc3545";
                            else if (t.type === "bonus") categoryColor = "#ffc107";
                            else if (t.type === "commission") categoryColor = "#17a2b8";
                            else if (t.type === "transfer") categoryColor = "#007bff";

                            return (
                              <tr key={t._id || i}>
                                <td>{formatDisplayDate(t.created_at)}</td>
                                <td>
                                  <span style={{ color: categoryColor, fontWeight: 'bold' }}>
                                    {t.type?.charAt(0).toUpperCase() + t.type?.slice(1) || "N/A"}
                                  </span>
                                </td>
                                <td className="text-center">
                                  {t.from_username || t.to_username || "System"}
                                </td>
                                <td className="text-center" style={{ color: '#28a745' }}>
                                  {isCredit ? `₹${currency(amount)}` : "—"}
                                </td>
                                <td className="text-center" style={{ color: '#dc3545' }}>
                                  {isDebit ? `₹${currency(amount)}` : "—"}
                                </td>
                                <td className="text-center">{t.remark || "N/A"}</td>
                                <td className="text-center">{t.tr_status || "N/A"}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination for Account */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">

                      {/* Showing Entries */}
                      <div className="sohwingallentries">
                        Showing{" "}
                        {(currentPage - 1) * applied.size + 1}
                        {" "}to{" "}
                        {(currentPage - 1) * applied.size + accountData.length}
                        {" "}of{" "}
                        {totalItems} entries
                      </div>

                      {/* Pagination */}
                      <div className="paginationall d-flex align-items-center gap-2">

                        {/* PREV */}
                        <button
                          className="d-flex justify-content-center align-items-center"
                          disabled={currentPage === 1}
                          onClick={handlePrev}
                        >
                          <MdOutlineKeyboardArrowLeft />
                        </button>

                        {/* PAGE NUMBERS */}
                        <div className="d-flex gap-1">
                          {getPageNumbers().map((pageNo) => (
                            <button
                              key={pageNo}
                              className={`paginationnumber ${pageNo === currentPage ? "active" : ""
                                }`}
                              onClick={() => handlePageClick(pageNo)}
                            >
                              {pageNo}
                            </button>
                          ))}
                        </div>

                        {/* NEXT */}
                        <button
                          className="d-flex justify-content-center align-items-center"
                          disabled={currentPage === totalPages}
                          onClick={handleNext}
                        >
                          <MdOutlineKeyboardArrowRight />
                        </button>

                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}